import { Button, Modal, ModalActions } from '@lokalise/louis';
import { useMemo } from 'react';

import { useStyleGuideContext } from '../../../StyleGuideContext';
import {
    type FileUpload,
    isFileUploadRemoved,
    isFileUploadStaged,
    isFileUploadSuccess,
} from '../../../model/fileUpload';
import { Dropzone } from '../../Dropzone';
import { IconPlaceholder, StyleGuideGrid, StyleGuideGridHeaderRow, StyleGuideModalBody } from '../styles';
import FileUploadItem from './FileUploadItem';

const canHaveLanguageAssigned = (fileUpload: FileUpload) =>
    isFileUploadSuccess(fileUpload) || isFileUploadRemoved(fileUpload) || isFileUploadStaged(fileUpload);

type UploadWizardProps = {
    fileUploads: FileUpload[] | null;
    onUploadRemoved: (fileUpload: FileUpload) => void;
    onAssignFiles: () => void;
    onCancel: () => void;
};

const UploadWizard = ({ fileUploads, onUploadRemoved, onAssignFiles, onCancel }: UploadWizardProps) => {
    const canAssignLanguages = useMemo(
        () => fileUploads !== null && fileUploads.every(canHaveLanguageAssigned),
        [fileUploads],
    );

    const hasUploadedFiles = useMemo(
        () => fileUploads !== null && fileUploads.some((upload) => !isFileUploadRemoved(upload)),
        [fileUploads],
    );

    const { uploadStyleGuideFiles } = useStyleGuideContext();

    return (
        <Modal title="Upload style guides" opened onClose={onCancel}>
            <StyleGuideModalBody>
                <Dropzone additional={hasUploadedFiles} onDrop={uploadStyleGuideFiles} />

                {hasUploadedFiles && (
                    <StyleGuideGrid>
                        <StyleGuideGridHeaderRow>
                            <IconPlaceholder />
                            <span>Name</span>
                            <span>Upload progress</span>
                        </StyleGuideGridHeaderRow>

                        {fileUploads?.map((fileUpload) => (
                            <FileUploadItem
                                key={fileUpload.uploadId}
                                fileUpload={fileUpload}
                                hidden={isFileUploadRemoved(fileUpload)}
                                onFileUploadRemoved={onUploadRemoved}
                            />
                        ))}
                    </StyleGuideGrid>
                )}
            </StyleGuideModalBody>

            {hasUploadedFiles && (
                <ModalActions>
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button disabled={!canAssignLanguages} variant="primary" onClick={onAssignFiles}>
                        Assign languages
                    </Button>
                </ModalActions>
            )}
        </Modal>
    );
};

export default UploadWizard;
